import { useCallback, useContext, useMemo } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentButton from '../../../draft-content-transplant/ContentButton';
import InsertContentDecorator from '../../../draft-transplant/InsertContentDecoratorV2';
import SnippetsPopoverErrorState from './draft-content-transplant-snippets/SnippetsPopoverErrorState';
import SnippetsPopoverEmptyState from './draft-content-transplant-snippets/SnippetsPopoverEmptyState';
import transformSnippetsData from './draft-content-transplant-snippets/transformSnippetsData';
import * as SnippetsApi from './draft-content-transplant-snippets/SnippetsApi';
import contentOutputs from '../../editorCapabilities/ContentOutputTypes';
import { RTEToolbarButton } from '../../components/StyledTextToolbarButton';
import { TextToolbarIcon } from 'rich-text-lib/components/editor/TextToolbarIcon';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import hubHttpClient from 'hub-http/clients/apiClient';
import PortalIdParser from 'PortalIdParser';
import { insertContent } from '../../utils/documentUtils';
import { convertToPlaintextState } from '../../utils/plaintextUtils';
import ContentContainer from '../../components/insertMenu/ContentContainer';
import identity from 'transmute/identity';
import GenericPopoverHeader from '../../components/GenericPopoverHeader';
import { getNewSnippetsUrl, getSnippetsUrl } from '../../utils/manageLinks';
import { EditorConfigContext } from '../../contexts/EditorConfigContext';
import { getGlobalVars } from '../../utils/globalVarUtil';
import { TextSnippetIcon } from 'foundations-assets/icons/components/TextSnippetIcon';

/** Configuration for the Snippets insert capability (saved text with personalization) */
import { jsx as _jsx } from "react/jsx-runtime";
const SnippetsButton = ({
  onClick
}) => /*#__PURE__*/_jsx(Tooltip, {
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "hubspot-prosemirror.tooltips.insertSnippet"
  }),
  children: /*#__PURE__*/_jsx(RTEToolbarButton, {
    onClick: onClick,
    selected: false,
    children: /*#__PURE__*/_jsx(TextToolbarIcon, {
      icon: TextSnippetIcon
    })
  })
});
export default function InsertSnippet({
  onInsertContent = identity,
  onOpenPopover,
  apiWrapper = SnippetsApi.defaultApiWrapper,
  apiClient = hubHttpClient,
  outputType = contentOutputs.EDITABLE,
  contactEmail,
  contactVid,
  supplementalObjects,
  PopoverErrorState = SnippetsPopoverErrorState,
  PopoverEmptyState = SnippetsPopoverEmptyState,
  Button = SnippetsButton,
  Header = GenericPopoverHeader,
  renderedPopoverClassname,
  useContentContainer,
  onViewInsertMenu = () => {},
  onInsertComplete = () => {}
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  const {
    usePlaintextMode,
    plaintextOptions
  } = getGlobalVars(useContext(EditorConfigContext));
  const insertSnippet = useCallback(({
    htmlBody
  }) => {
    const newState = insertContent({
      htmlBody,
      editorState,
      from: editorState.selection.from,
      to: editorState.selection.to
    });
    setEditorState(usePlaintextMode ? convertToPlaintextState(newState, plaintextOptions) : newState);
    onInsertComplete();
  }, [editorState, setEditorState, usePlaintextMode, plaintextOptions, onInsertComplete]);
  const SnippetsPopover = useMemo(() => {
    const apiFunctions = apiWrapper(apiClient);
    const insertSnippetDecoratorConfig = {
      outputType,
      apiFunctions,
      mergeTagOptions: {
        includeCustomTokens: true
      }
    };
    if (useContentContainer) {
      return InsertContentDecorator(ContentContainer({
        onInsertContent,
        // @ts-expect-error portalId can be undefined in unit testing for example
        portalId: PortalIdParser.get(),
        PopoverErrorState,
        PopoverEmptyState,
        Header,
        searchPlaceHolder: 'draftPlugins.snippetsPlugin.popover.search.placeholder',
        transformData: transformSnippetsData,
        search: apiFunctions.searchSnippets || SnippetsApi.searchSnippets,
        onViewInsertMenu,
        // @ts-expect-error portalId can be undefined in unit testing for example
        manageUrl: getSnippetsUrl(PortalIdParser.get()),
        getNewLinkUrl: getNewSnippetsUrl,
        titleMessage: 'draftPlugins.snippetsPlugin.popover.header.insertSnippet',
        contentKey: 'snippets'
      }), insertSnippetDecoratorConfig);
    }
    return InsertContentDecorator(ContentButton({
      onInsertContent,
      // @ts-expect-error ts-migrate FIXME
      portalId: PortalIdParser.get(),
      Button,
      PopoverErrorState,
      PopoverEmptyState,
      onOpenPopover,
      Header,
      renderedPopoverClassname,
      searchPlaceHolder: 'draftPlugins.snippetsPlugin.popover.search.placeholder',
      transformData: transformSnippetsData,
      search: apiFunctions.searchSnippets || SnippetsApi.searchSnippets,
      popoverTestKey: 'snippets-popover',
      // @ts-expect-error portalId can be undefined in unit testing for example
      manageUrl: getSnippetsUrl(PortalIdParser.get()),
      getNewLinkUrl: getNewSnippetsUrl,
      titleMessage: 'draftPlugins.snippetsPlugin.popover.header.insertSnippet'
    }), insertSnippetDecoratorConfig);
  }, [onInsertContent, onOpenPopover, apiClient, apiWrapper, outputType, PopoverErrorState, PopoverEmptyState, Header, renderedPopoverClassname, Button, onViewInsertMenu, useContentContainer]);
  return /*#__PURE__*/_jsx(SnippetsPopover, {
    contactEmail: contactEmail
    // @ts-expect-error migration
    ,
    contactVid: contactVid,
    supplementalObjects: supplementalObjects,
    onChange: insertSnippet,
    useProsemirror: true
  });
}